/**
 * themeMode — 深/淺色主題的**唯一**掛載點(R218;D3 08-07 確認清單 D 節)
 * -----------------------------------------------------------------------------
 * 同構自 D3 `theme.js`(9 行):讀偏好 → 在 `<html>` 掛 `data-theme="dark|light"`,
 * `system` 跟隨作業系統並掛 `matchMedia` change listener 即時切換。
 * 對應的 token 覆蓋在 [theme.css](./theme.css) 的 `html[data-theme='dark'], .theme-dark`。
 *
 * **兩個方向都要能贏**(本輪驗收條件):
 *   1. 跟隨系統 —— 沒存過偏好(預設 `system`)時完全跟著 `prefers-color-scheme`,
 *      使用者在 OS 切換時**不用重整**(mq listener)。
 *   2. 明確覆蓋 —— `localStorage['hai.theme'] = 'dark' | 'light'` 之後系統偏好不再有效。
 *      這是本輪唯一的覆蓋入口(QA / dev console 用):
 *        `localStorage.setItem('hai.theme','dark'); location.reload()`
 *
 * **本輪刻意不做使用者可切的 UI 開關** —— 個人資料燈箱的「外觀三段(系統/淺色/深色)」
 * 屬 App shell 範疇,08-07 清單標明本輪不展開。那顆做起來時直接呼叫 `setThemeMode()`
 * 即可(不需要動 CSS,也不需要動這支的 resolve 邏輯)。
 *
 * **為什麼不放 `prefsStore`**:`prefsStore.load()` 是 async(Promise),主題必須在
 * 第一次 paint **之前**同步決定,否則會閃一下淺色再翻黑(FOUC)。所以走自己的同步
 * localStorage 鍵;之後真要跟 server 偏好對齊,在 `setThemeMode` 內再寫一份即可。
 */

/** 使用者偏好(`system` = 跟隨作業系統)。 */
export type ThemePref = 'system' | 'light' | 'dark';

/** 最終套用到 `<html data-theme>` 的值(`system` 已被解析掉)。 */
export type ResolvedTheme = 'light' | 'dark';

/** localStorage 鍵(沿用 repo `hai.*` 慣例)。 */
export const THEME_PREF_KEY = 'hai.theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

const VALID_PREFS: readonly ThemePref[] = ['system', 'light', 'dark'];

/**
 * 把任意值正規化成合法偏好。
 *
 * ⚠️ **R48 舊值 `'moon'` / `'sun'` 一律 drop 成 `'system'`** —— 那組是 R48 砍掉的
 * 雙軸主題(月亮/太陽),值域跟現在完全不同。存量使用者的 localStorage 可能還留著,
 * 若誤認成合法值會開出隨機主題。
 */
export function normalizeThemePref(raw: unknown): ThemePref {
  return VALID_PREFS.includes(raw as ThemePref) ? (raw as ThemePref) : 'system';
}

/** 純函式:偏好 + 系統是否深色 → 實際主題。給測試與呼叫端共用,不碰 DOM。 */
export function resolveTheme(pref: ThemePref, prefersDark: boolean): ResolvedTheme {
  if (pref === 'dark') return 'dark';
  if (pref === 'light') return 'light';
  return prefersDark ? 'dark' : 'light';
}

/** 讀目前偏好(localStorage 不可用 / 髒值 → `'system'`)。 */
export function readThemePref(): ThemePref {
  try {
    return normalizeThemePref(localStorage.getItem(THEME_PREF_KEY));
  } catch {
    // Safari 無痕 / storage 被封鎖:當作沒設過,跟隨系統。
    return 'system';
  }
}

/** 目前系統是否偏好深色(jsdom / 舊瀏覽器無 matchMedia → false)。 */
export function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(DARK_QUERY).matches === true;
}

/**
 * 把偏好套到 `<html>`:
 *   - `data-theme`      = 解析後的 `light|dark`(CSS selector 吃這個)
 *   - `data-theme-pref` = 原始偏好(含 `system`;之後外觀 UI 要回顯選中項用得到)
 *
 * @returns 實際套用的主題
 */
export function applyThemeMode(pref: ThemePref = readThemePref()): ResolvedTheme {
  const resolved = resolveTheme(pref, systemPrefersDark());
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    root.setAttribute('data-theme', resolved);
    root.setAttribute('data-theme-pref', pref);
  }
  return resolved;
}

/** 寫入偏好 + 立即套用(未來「外觀三段」UI 的唯一出口)。 */
export function setThemeMode(pref: ThemePref): ResolvedTheme {
  try {
    localStorage.setItem(THEME_PREF_KEY, pref);
  } catch {
    // storage 被封鎖時仍套用本次(只是不持久化)。
  }
  return applyThemeMode(pref);
}

/**
 * 開機掛載:先套一次,再訂閱系統偏好變化。
 *
 * listener **不做 `pref === 'system'` 的提前 return** —— 而是每次重讀偏好再解析:
 * 使用者可能在別的分頁改過 `hai.theme`,重讀才不會用到過期的閉包值。
 *
 * @returns 解除訂閱(HMR / 測試用;正常執行期不需要呼叫)
 */
export function initThemeMode(): () => void {
  applyThemeMode();

  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }
  const mq = window.matchMedia(DARK_QUERY);
  const onChange = () => {
    applyThemeMode(readThemePref());
  };

  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }
  // Safari < 14 只有 deprecated 的 addListener
  if (typeof mq.addListener === 'function') {
    mq.addListener(onChange);
    return () => mq.removeListener?.(onChange);
  }
  return () => {};
}
